/**
 * tm/components/RecNotifyBox.tsx — 통화 녹음을 올린 뒤 담당 팀장님께 문자(PC)
 *  2026-10-28 지도사 「업로드가 완료되면 내가 팀장님에게 해당 사실을 알릴 수 있도록 문자 보내기 버튼」
 *  동행상담 녹음은 따로 문자 없음(동행상담 결과 보내기로 함께 간다 · 지도사) · 본문은 정해진 안내 글(지도사 메모는 안 들어감)
 *  받는 분 고르기(기본 = 보낼 수 있는 분 모두) · 미리보기 · 정리가 끝났으면 「보내기」 · 정리 중이면 「정리되면 보내기」(서버가 끝나면 보냄)
 *  휴대폰(/m)은 같은 서버 · 같은 문자를 TDS 모양으로(pages/MobileCallList)
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { C, R, T } from './ui';
import type { CallNotify, CallTarget } from '../callRec';
import { honorOf, noticeText, notifyCall, notifyLine, notifyToast } from '../callRec';

const btn = (x?: CSSProperties): CSSProperties => ({ height: 30, padding: '0 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', ...x });

export default function RecNotifyBox({ recId, status, notify, targets, companyName, onSent, onToast, keyReady = true }: {
  recId: string; status: string; notify: CallNotify | null | undefined; /** 예전 호출 호환(이제 안 씀 — 통화 주제는 카드 머리) */ memo?: string; keyReady?: boolean;
  targets: CallTarget[]; companyName: string; onSent: () => void; onToast: (m: string, t?: 'success' | 'error') => void;
}) {
  const sendable = targets.filter(t => !t.why);
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const done = status === 'done';
  const line = notifyLine(notify || null, targets, keyReady);
  const sentBefore = !!notify && ['sent', 'partial'].includes(notify.state);
  const pending = !!notify && notify.state === 'pending';
  if (!targets.length) return line ? <div style={{ fontSize: T.sm, color: C.sub, margin: '6px 2px 0' }}>{line}</div> : null;
  const send = async () => {
    if (!ids.length || busy) return;
    setBusy(true);
    try {
      const r = await notifyCall(recId, ids, !done);
      onToast(notifyToast(r.state), r.state === 'failed' ? 'error' : 'success');
      setOpen(false); onSent();
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '보내지 못했어요', 'error'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ margin: '6px 2px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: T.sm, color: C.sub, lineHeight: 1.5 }}>{line}</span>{/* 통화 주제는 녹음 카드 머리에 있어 여기선 빼요(같은 글 두 번 · 2026-10-28) */}
        {sendable.length > 0 && (
          <button type="button" style={btn(open ? {} : { background: C.accentLt, borderColor: C.accent, color: C.accent })} onClick={() => { setOpen(o => !o); setIds(sendable.map(t => t.id)); }}>
            {open ? '닫기' : sentBefore ? '문자 다시 보내기' : pending ? '받는 분 바꾸기' : '팀장님께 문자'}
          </button>
        )}
      </div>
      {open && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, padding: 10, marginTop: 6, background: C.bgAlt, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {targets.map(t => {
              const on = ids.includes(t.id), can = !t.why;
              return (
                <button key={t.id} type="button" disabled={!can} aria-pressed={on} onClick={() => setIds(v => (on ? v.filter(x => x !== t.id) : [...v, t.id]))}
                  style={btn({ borderColor: on ? C.accent : C.border, background: on ? C.accentLt : C.white, color: !can ? C.muted : on ? C.accent : C.sub, cursor: can ? 'pointer' : 'default' })}>
                  {on ? '✓ ' : ''}{honorOf(t)}{t.why ? ` · ${t.why}` : ''}
                </button>
              );
            })}
          </div>
          {ids.length > 0 && <div style={{ fontSize: T.sm, color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.6, padding: '8px 10px', background: C.white, borderRadius: R.sm, border: `1px solid ${C.border}` }}>{noticeText(companyName, targets.find(t => t.id === ids[0]) || { name: '' })}</div>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" disabled={!ids.length || busy} onClick={() => void send()} style={btn({ background: C.accent, borderColor: C.accent, color: '#fff', opacity: ids.length && !busy ? 1 : 0.5 })}>
              {busy ? '보내는 중' : done ? `${ids.length}명에게 보내기` : `정리되면 ${ids.length}명에게 보내기`}
            </button>
            {!done && <span style={{ fontSize: T.xs, color: C.muted }}>대화 글로 정리되는 중이에요 · 끝나면 저절로 보내요</span>}
          </div>
        </div>
      )}
    </div>
  );
}
